import _t from 'i18n!nls/common';

const REDIRECT_ATTR = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect';
const ENTITY_ATTR = 'entityID';
const VALID_UNTIL_ATTR = 'validUntil';
const URL_REGEX =
  /^(?:(?:https?|ftp):)?\/\/(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4])|(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*\.[a-z\u00a1-\uffff]{2,})(?::\d{2,5})?(?:[/?#]\S*)?$/i;

const getYesterday = (dateObj: Date) => {
  dateObj.setDate(dateObj.getDate() - 1);
  return dateObj;
};

const getNextWeek = (dateObj: Date) => {
  dateObj.setDate(dateObj.getDate() + 7);
  return dateObj;
};

const getMetadataValidationMessage = (
  doc: Document,
  thirdPartyOrganization: { entityId?: string | null; refreshUrl?: string | null }
): string | null => {
  let message: string | null = null;
  const { entityId, refreshUrl } = thirdPartyOrganization;

  // Has a SSO redirect URL to send users to after they click "Log in with SSO"
  const redirectElem = doc.querySelectorAll(`[Binding="${REDIRECT_ATTR}"]`)[0];
  const hasRedirectLocation = redirectElem && redirectElem.getAttribute('Location');

  // Has a entityID that matches the one configured for the given thirdPartyOrganization
  const orgEntityId = entityId;
  const entityElem = doc.querySelectorAll(`[${ENTITY_ATTR}="${orgEntityId}"]`)[0];
  const hasMatchingEntityId = !!entityElem;

  // Has a timestamp that is valid (not expired)
  const validUntilElem = doc.querySelectorAll(`[${VALID_UNTIL_ATTR}]`)[0];
  const doesValidUntilParamExist = !!validUntilElem;
  const validUntilAttributeValue = doesValidUntilParamExist && validUntilElem.getAttribute(VALID_UNTIL_ATTR);
  const timestampFromMetadata = validUntilAttributeValue && new Date(validUntilAttributeValue).getTime();
  const hasTimestampExpired = timestampFromMetadata && timestampFromMetadata < new Date().getTime();

  if (!hasRedirectLocation) {
    // using string joins instead of purely backticks because alert() cares about whitespace
    message = _t(
      'The metadata file does not contain a binding for the "#{redirectAttribute}" attribute. This is needed to determine where to redirect the user once they click the "Log In with SSO" button. Please update the metadata file.',
      { redirectAttribute: REDIRECT_ATTR }
    );
  } else if (!hasMatchingEntityId) {
    message = _t(
      'The metadata file\'s "#{entityAttribute}" does not match the thirdPartyOrganization\'s "#{entityAttribute}" value of "#{entityId}". Please update the thirdPartyOrganization entry and/or the metadata file.',
      { entityAttribute: ENTITY_ATTR, entityId: String(orgEntityId) }
    );
  } else if (doesValidUntilParamExist && hasTimestampExpired) {
    message = _t(
      'The metadata file\'s "#{validUntilAttribute}" timestamp has expired. Please update the thirdPartyOrganization entry and/or the metadata file.',
      { validUntilAttribute: VALID_UNTIL_ATTR }
    );
  } else if (refreshUrl) {
    message = _t('Cannot upload XML file. Must provide either a SAML metadata URL or a XML metadata file, not both.');
  }

  return message;
};

const isValidUrl = (string: string) => {
  try {
    new URL(string);
  } catch {
    return false;
  }
  return URL_REGEX.test(string);
};

function extractEntityId(doc: Document) {
  const entityDescriptorNode = doc.getElementsByTagNameNS(
    'urn:oasis:names:tc:SAML:2.0:metadata',
    'EntityDescriptor'
  )[0];
  // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return entityDescriptorNode.getAttribute('entityID');
}

async function validateEntityId(xmlDoc: Document, expectedEntityId: string | undefined): Promise<void> {
  if (!expectedEntityId) {
    throw 'ExpectedEntityId is missing';
  }

  const entityIdFromXml = extractEntityId(xmlDoc);

  if (entityIdFromXml !== expectedEntityId) {
    throw `Entity ID from XML (${entityIdFromXml}) does not match the expected Entity ID (${expectedEntityId})`;
  }
}

async function fetchMetadata(url: string): Promise<Blob> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw `Failed to fetch the URL ${url}.`;
    }
    return response.blob();
  } catch (error) {
    throw error.toString();
  }
}

async function validateEntityIdMatchesMetadata(
  metadataUrl: string,
  entityId: string | null | undefined
): Promise<void> {
  if (!entityId) {
    throw 'Failed to validate Metadata URL: Entity ID is missing.';
  }
  const metadataBlob = await fetchMetadata(metadataUrl);
  const xmlDoc = await readFileContent(new File([metadataBlob], 'metadata.xml'));
  await validateEntityId(xmlDoc, entityId);
}

export async function readFileContent(file: File | null | undefined): Promise<Document> {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject('No file selected');
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(event.target?.result as string, 'text/xml');
      resolve(xmlDoc);
    };

    reader.onerror = () => {
      reject('Error reading file');
    };

    reader.readAsText(file);
  });
}

export default {
  getMetadataValidationMessage,
  getYesterday,
  getNextWeek,
  validateEntityId,
  validateEntityIdMatchesMetadata,
  isValidUrl,
  constants: {
    REDIRECT_ATTR,
    ENTITY_ATTR,
    VALID_UNTIL_ATTR,
  },
};
